import React, {useRef, useEffect, useState} from 'react'
import {select, scaleLinear, scaleBand} from 'd3'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'

// The original fixed margins, expressed per side so a caller can override an individual side
// (e.g. a horizontal chart needing a wide left gutter for its labels).
const DEFAULT_MARGIN = {top: 8, right: 60, bottom: 52, left: 60}

function D3Chart(props) {
  const classes = useStyles()
  const container = useRef(null)
  const [minWidth, setMinWidth] = useState(0)

  useEffect(() => {
    const draw = () => {
      setMinWidth(determineMinWidth(props.xAxisLabels, props.formatXAxis))
      props.onReady(initChart(container.current, props.margin))
    }
    let resizeTimeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      // to avoid calling this too many times, we set it in a timeout
      resizeTimeout = setTimeout(draw, 300)
    }
    window.addEventListener('resize', handleResize)
    // interactive charts draw right away; everything else keeps the original debounced initial draw
    if (props.immediate) {
      draw()
    } else {
      handleResize()
    }

    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [props.xAxisLabels, props.formatXAxis, props.onReady, props.margin, props.immediate])

  return (
    <div className={classes.outerContainer}>
      <div className={classes.container} ref={container} id={props.id} style={{minWidth: `${minWidth}px`}} />
    </div>
  )
}

D3Chart.propTypes = {
  onReady: PropTypes.func.isRequired,
  id: PropTypes.string,
  xAxisLabels: PropTypes.arrayOf(PropTypes.string),
  formatXAxis: PropTypes.func,
  // per-side overrides of DEFAULT_MARGIN
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  // draw synchronously rather than on the resize debounce (interactive charts)
  immediate: PropTypes.bool,
}

const useStyles = makeStyles(theme => ({
  container: {
    lineHeight: 0,
    height: '100%',
  },
  outerContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    position: 'relative',
    height: '100%',
  },
}))

// -----------------------------------------------------------------------------------
// Helper functions

/**
 * @param {HTMLElement} elem
 * @param {?Object} marginProp per-side overrides of DEFAULT_MARGIN
 */
function initChart(elem, marginProp) {
  if (!elem) return [undefined, undefined, undefined]

  const margin = {...DEFAULT_MARGIN, ...(marginProp || {})}
  const width = elem.clientWidth
  const height = elem.clientHeight || 500

  // clear the element
  select(elem).html('')

  const canvas = select(elem)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`)

  // the canvas height and width is the area the graph will actually draw.
  // It will not include axis labels so we need to make sure there's enough space
  canvas.width = width - margin.left - margin.right
  canvas.height = height - margin.top - margin.bottom

  const x = scaleBand().range([0, canvas.width])
  const y = scaleLinear().range([canvas.height, 0])

  return [canvas, x, y]
}

/**
 * @param {Array<string>} labels
 * @param {function?} formatXAxis
 */
function determineMinWidth(labels, formatXAxis) {
  if (!Array.isArray(labels)) {
    return 0
  }

  // Initialize max width of label to be 20 pixels. We will return the maxWidthOfLabel
  // multiplied by the number of labels to determine the minX of the canvas
  let maxWidthOfLabel = 30
  labels.forEach(l => {
    if (formatXAxis) {
      l = formatXAxis(l)
    }
    let widthOfDataXAxisLabel = estimateWidthOfLabelBasedOnNumberOfCharacters(l)
    maxWidthOfLabel = Math.max(widthOfDataXAxisLabel, maxWidthOfLabel)
  })
  return maxWidthOfLabel * labels.length
}

/**
 * @param {String} label
 */
function estimateWidthOfLabelBasedOnNumberOfCharacters(label) {
  let lengthOfLabel = label.length
  let estimatedWidthOfCharacter = 5 // Estimate a character to have a width of 5 pixels
  return lengthOfLabel * estimatedWidthOfCharacter
}

export default D3Chart
